/**
 * Sticky Bar
 * Reveals a bottom-pinned section once the page is scrolled past a threshold.
 * Pairs with sections/sticky-bar.liquid (CSS handles the show/hide via .is-visible).
 */
class StickyBar extends HTMLElement {
  #lastHeight = null;
  #resizeObserver = null;
  #controller = null;
  #sectionSelected = false;
  #blockSelected = false;

  connectedCallback() {
    // Publish the bar's height so the last section can reserve space for it
    // instead of being overlapped by the fixed bar.
    this.#syncHeight();
    this.#resizeObserver = new ResizeObserver(this.#syncHeight);
    this.#resizeObserver.observe(this);

    this.#controller = new AbortController();
    const { signal } = this.#controller;

    this.threshold = parseInt(this.dataset.scrollThreshold, 10) || 0;
    this.#update();
    window.addEventListener('scroll', this.#update, { passive: true, signal });

    // In the theme editor the bar follows the threshold like the storefront, but stays
    // shown while the section or one of its blocks is selected so it can be edited.
    if (window.Shopify?.designMode) {
      document.addEventListener('shopify:section:select', this.#onSectionSelect, { signal });
      document.addEventListener('shopify:section:deselect', this.#onSectionDeselect, { signal });
      this.addEventListener('shopify:block:select', this.#onBlockSelect, { signal });
      this.addEventListener('shopify:block:deselect', this.#onBlockDeselect, { signal });
    }
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#resizeObserver?.disconnect();
    document.documentElement.style.removeProperty('--sticky-bar-height');
  }

  // `>=` so a threshold of 0 means "always visible", including at the top of the
  // page and on pages too short to scroll.
  #update = () => {
    const selected = this.#sectionSelected || this.#blockSelected;
    this.classList.toggle('is-visible', selected || window.scrollY >= this.threshold);
  };

  #onSectionSelect = (event) => {
    if (!event.target.contains(this)) return;
    this.#sectionSelected = true;
    this.#update();
  };

  #onSectionDeselect = (event) => {
    if (!event.target.contains(this)) return;
    this.#sectionSelected = false;
    this.#update();
  };

  #onBlockSelect = () => {
    this.#blockSelected = true;
    this.#update();
  };

  #onBlockDeselect = () => {
    this.#blockSelected = false;
    this.#update();
  };

  // `offsetHeight` is 0 when the bar is hidden by its mobile/desktop media query,
  // which zeroes the reserved space automatically.
  #syncHeight = () => {
    const height = this.offsetHeight;
    if (height === this.#lastHeight) return;
    this.#lastHeight = height;
    document.documentElement.style.setProperty('--sticky-bar-height', `${height}px`);
  };
}

if (!customElements.get('sticky-bar')) {
  customElements.define('sticky-bar', StickyBar);
}
